import { useId } from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { Textarea, View } from "@/components/ui";
import { cn } from "@/lib/utils";

import { FormControlError } from "../form-control-error";
import { Typography } from "../typography";

type TextareaFormControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string;
  className?: string;
  placeholder?: string;
  maxLength?: number;
  showCharCount?: boolean;
  hideErrorMessage?: boolean;
};

export const TextareaFormControl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  className,
  placeholder,
  maxLength = 200,
  showCharCount = true,
  hideErrorMessage,
  ...props
}: TextareaFormControlProps<TFieldValues, TName>) => {
  const id = useId();
  return (
    <View className={cn("pb-3", className)}>
      {label && (
        <Typography htmlFor={id} variant="label-sm" className="block pb-1">
          {label}
        </Typography>
      )}
      <Controller
        {...props}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Textarea
              value={value ?? ""}
              onChangeText={onChange}
              placeholder={placeholder}
              maxLength={maxLength}
              className={cn(error ? "border-destructive" : "border-secondary-border")}
            />
            {showCharCount && (
              <Typography variant="body-sm" color="foreground-intermediate" className="mt-1">
                Max {maxLength} characters
              </Typography>
            )}
            {!hideErrorMessage && <FormControlError error={error?.message} />}
          </>
        )}
      />
    </View>
  );
};
